import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  SelectChangeEvent,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import { ClothingItem, ClothingType, OccasionType } from "../types/wardrobe";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import { api } from "../services/api";
import { useResponsive } from "../hooks/useResponsive";

interface Props {
  item: ClothingItem;
  onUpdate: (updatedItem: ClothingItem) => void;
  onDelete?: (item: ClothingItem) => void;
  isDemoMode?: boolean;
}

const SEASONS = ["spring", "summer", "autumn", "winter"];
const CLOTHING_TYPES: ClothingType[] = [
  "top",
  "bottom",
  "dress",
  "outerwear",
  "shoes",
  "accessory",
];
const OCCASIONS: OccasionType[] = ["casual", "formal", "business", "sport"];

const WardrobeItem: React.FC<Props> = ({
  item,
  onUpdate,
  onDelete,
  isDemoMode = false,
}) => {
  const { isMobile } = useResponsive();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedItem, setEditedItem] = useState<ClothingItem>({
    ...item,
    seasons: item.seasons?.map((s) => s.toLowerCase()),
  });
  const [isReanalyzing, setIsReanalyzing] = useState(false);

  const handleEditOpen = () => {
    setEditedItem({
      ...item,
      seasons: item.seasons?.map((s) => s.toLowerCase()),
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
  };

  const handleTypeChange = (event: SelectChangeEvent<ClothingType>) => {
    setEditedItem({
      ...editedItem,
      type: event.target.value as ClothingType,
    });
  };

  const handleSeasonsChange = (event: SelectChangeEvent<string[]>) => {
    setEditedItem({
      ...editedItem,
      seasons: event.target.value as string[],
    });
  };

  const handleOccasionsChange = (event: SelectChangeEvent<OccasionType[]>) => {
    setEditedItem({
      ...editedItem,
      occasions: event.target.value as OccasionType[],
    });
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedItem({
      ...editedItem,
      name: event.target.value,
    });
  };

  const handleSave = async () => {
    try {
      const updatedItem = await api.updateClothingItem(editedItem);
      onUpdate(updatedItem);
    } catch (error) {
    } finally {
      setEditDialogOpen(false);
    }
  };

  const handleReanalyze = async () => {
    try {
      setIsReanalyzing(true);
      const updatedItem = await api.reanalyzeItem(item._id);
      onUpdate(updatedItem);
    } catch (error) {
    } finally {
      setIsReanalyzing(false);
    }
  };

  return (
    <>
      <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <CardMedia
          component="img"
          height={isMobile ? "150" : "200"}
          image={item.imageUrl}
          alt={item.name}
          sx={{
            objectFit: "contain",
            backgroundColor: "#f5f5f5",
          }}
        />
        <CardContent sx={{ flexGrow: 1, p: isMobile ? 1 : 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Typography variant={isMobile ? "subtitle1" : "h6"} component="div">
              {item.name}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip
                title={
                  isDemoMode ? "Editing is disabled in demo mode" : "Edit item"
                }
                arrow
              >
                <span>
                  <IconButton
                    size={isMobile ? "small" : "small"}
                    onClick={handleEditOpen}
                    disabled={isDemoMode}
                  >
                    <EditIcon fontSize={isMobile ? "small" : "small"} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  isDemoMode
                    ? "Re-analysis is disabled in demo mode"
                    : "Re-analyze item"
                }
                arrow
              >
                <span>
                  <IconButton
                    size={isMobile ? "small" : "small"}
                    onClick={handleReanalyze}
                    disabled={isReanalyzing || isDemoMode}
                    color="primary"
                  >
                    {isReanalyzing ? (
                      <CircularProgress size={isMobile ? 16 : 20} />
                    ) : (
                      <RefreshIcon fontSize={isMobile ? "small" : "small"} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              {onDelete && (
                <Tooltip
                  title={
                    isDemoMode
                      ? "Deletion is disabled in demo mode"
                      : "Delete item"
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      size={isMobile ? "small" : "small"}
                      onClick={() => onDelete(item)}
                      disabled={isDemoMode}
                      color="error"
                    >
                      <DeleteIcon fontSize={isMobile ? "small" : "small"} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: isMobile ? 0.5 : 1,
              mb: 1,
            }}
          >
            {item.seasons.map((season) => (
              <Chip
                key={season}
                label={season}
                size={isMobile ? "small" : "small"}
                color="secondary"
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                }}
              />
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: isMobile ? 0.5 : 1,
              mb: 2,
            }}
          >
            {item.occasions.map((occasion) => (
              <Chip
                key={occasion}
                label={occasion}
                size={isMobile ? "small" : "small"}
                color={
                  occasion === "formal"
                    ? "error"
                    : occasion === "business"
                    ? "warning"
                    : occasion === "sport"
                    ? "success"
                    : "default"
                }
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Item</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={editedItem.name}
              onChange={handleNameChange}
              variant="outlined"
            />

            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={editedItem.type}
                onChange={handleTypeChange}
                input={<OutlinedInput label="Type" />}
              >
                {CLOTHING_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Seasons</InputLabel>
              <Select
                multiple
                value={editedItem.seasons || []}
                onChange={handleSeasonsChange}
                input={<OutlinedInput label="Seasons" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {SEASONS.map((season) => (
                  <MenuItem key={season} value={season}>
                    {season.charAt(0).toUpperCase() + season.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Occasions</InputLabel>
              <Select
                multiple
                value={editedItem.occasions}
                onChange={handleOccasionsChange}
                input={<OutlinedInput label="Occasions" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {OCCASIONS.map((occasion) => (
                  <MenuItem key={occasion} value={occasion}>
                    {occasion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WardrobeItem;
